import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { Role } from "../../../models/role";
import { RoleControllerService } from "../../../services/role-controller/role-controller.service";
import { RoleService } from "../../../services/role-controller/role.service";
import { Store } from "../../../services/store.service";
import {TranslatedBaseError} from "../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";
import {Context} from "../../../models/context";
import {ServiceDetailComponent} from "../service-detail/service-detail.component";
import {Institution} from "../../../models/institution";
import {InstitutionControllerService} from "../../../services/institution-controller/institution-controller.service";
import {DeterminantControllerService} from "../../../services/registration-controller/determinant-controller.service";
import {RegistrationControllerService} from "../../../services/registration-controller/registration-controller.service";
import {Registration} from "../../../models/registration";
import {RoleRegistration} from "../../../models/role-registration";
import { map } from 'rxjs/operators';

@Component({
  selector: "app-roles",
  templateUrl: "./roles.component.html",
  providers: [RoleControllerService],
})
export class RolesComponent extends TranslatedBaseError implements OnInit, OnChanges {

  @ViewChild("ServiceDetailComponent") public serviceDetailComponent: ServiceDetailComponent;

  @Input() public roles: Role[];
  @Input() public hideList: boolean = false;
  @Input() public isRoot: boolean = true;
  @Input() public determinantMapById: { [key: string]: string } = {};
  @Input() public institutions: Institution[] = [];

  public menuRoles: Role[];
  public activeMenuRoles: Role[];
  public activeRoles: Role[];
  public ghostRoles: Role[];
  public orderedMenuRoles: any[] = [];
  public orderList: any[] = [];
  public serviceId: string;
  public selectedRoleId: string = null;
  public baseUrl: string;
  public showRoleAdd: boolean = false;
  public determinantFilters: any[] = null;
  public registrationFilters: any[] = null;
  public serviceRegistrations: Registration[] = [];
  public roleNameField: string;
  public isDeleted: boolean = false;
  public isStartRole: boolean = false;
  public context: Context = new Context();
  public rolesTutorialClosed: boolean = true;

  constructor(private roleController: RoleControllerService,
              private institutionControllerService: InstitutionControllerService,
              private determinantService: DeterminantControllerService,
              private registrationControllerService: RegistrationControllerService,
              private router: Router,
              private route: ActivatedRoute,
              toastr: ToastrService,
              public translateService: TranslateService,
              public store: Store) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.selectedRoleId = params.roleId || null;
      this.baseUrl = "/services/" + this.serviceId + "/roles";
    });

    if (this.isRoot) {
      this.getRolesList();
      this.getServiceDeterminantKeyName();
      this.loadInstitutions();
    }

    // Always load service registrations for the filter
    this.loadServiceRegistrations();

    // fix for leader line
    document.body.classList.add("leader-lines-index-0");
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.roles && changes.roles.currentValue) {
      this.setMenuRoles();
    }
  }

  public getRolesList() {
    this.roleController.getRolesDetailedList(this.serviceId).subscribe(
      (data) => {
        this.roles = data;
        this.setMenuRoles();
      },
    );
  }

  public showLines() {
    document.querySelectorAll(".flow-line").forEach((l) => {
      l.classList.remove("overlap");
    });
  }

  public hideLines() {
    document.querySelectorAll(".flow-line").forEach((l) => {
      l.classList.add("overlap");
    });
  }

  public onTutorialOpened(event) {
    console.log("Tutorial opened event received in roles component", event);
    this.hideLines();
    this.rolesTutorialClosed = false;
  }

  public onTutorialClosed(event) {
    console.log("Tutorial closed event received in roles component", event);
    this.rolesTutorialClosed = true;
    this.showLines();
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public loadInstitutions() {
    this.institutionControllerService.getInstitutions().subscribe(
      (data) => {
        this.institutions = data.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
        });
      },
      (error) => this.showErrorMessageWithTitle("Error HTTP Post Service", error),
    );
  }

  public loadServiceRegistrations() {
    this.registrationControllerService.getRegistrationsList(this.serviceId).subscribe(
      (data) => {
        this.serviceRegistrations = data;
      },
      (error) => this.showErrorMessageWithTitle("Error loading registrations", error),
    );
  }

  public onRegistrationSelectionChanged(event): void {
    this.registrationFilters = event;
    this.orderedMenuRoles = this.generateRolesList();
  }

  public addedRole() {
    this.closeLayer();
    this.getRolesList();
  }

  public updateRole(role) {
    const successCb = (data) => {
      role.isInRenameMode = false;
      role.isLoading = false;
      this.responseSuccessful("Saved successfully!");
    };
    const errorCb = (error) => {
      this.ngOnInit();
      return this.showErrorMessageWithTitle("Error during save/update of role!", error);
    };

    if (role.type === "BotRole") {
      return this.roleController.saveBotRole(role).subscribe(successCb, errorCb);
    } else if (role.type === "UserRole") {
      return this.roleController.saveUserRole(role).subscribe(successCb, errorCb);
    }
    throw new Error("Undefined role type!");
  }

  public findAsDestination(id) {
    return this.roles.find((r) => {
      return r.statuses?.find((s) => {
        return s.destinations?.find((d) => {
          return d.destinationId === id;
        });
      });
    });
  }

  public isorHasDestionation(role) {
    let isOrHas = false;
    if (role.statuses?.length) {
      isOrHas = role.statuses?.find((s) => {
        return s.name === "FILE VALIDATED" && s.destinations?.length;
      });
    }
    if (isOrHas) {
      return true;
    } else {
      return this.findAsDestination(role.id);
    }
  }

  public isPathParent(role, id) {
    return role.statuses?.find((s) => {
      return s.name === "FILE VALIDATED" && s.destinations?.some((d) => {
        return d.destinationId === id;
      });
    });
  }

  public checkPathValidity(role) {
    if (role.startRole) {
      return true;
    }
    let parentIsStartRole = false;
    const parents = this.roles.filter((r) => {
      return this.isPathParent(r, role.id);
    });
    if (parents.length) {
      parentIsStartRole = parents.some((p) => {
        return p.startRole || this.checkPathValidity(p);
      });
    } else {
      return false;
    }
    return parentIsStartRole;
  }

  public isParentStartingRole(roleId) {
    return this.roles.find((r) => {
      return r.id === roleId && r.startRole;
    });
  }

  // applicant shall be filtered out
  public setMenuRoles(): void {
    this.menuRoles = RolesComponent.filterUnnecessaryRoles(this.roles);
    this.menuRoles = RolesComponent.filterGhostRoles(this.menuRoles);
    this.menuRoles = RolesComponent.filterFEBotRoles(this.menuRoles);

    const rolesList = [];
    this.activeMenuRoles = this.menuRoles;
    this.activeMenuRoles.forEach((role) => {
      if (role.statuses && role.statuses.length > 0) {
        role.statuses.forEach((status) => {
          if (status.name === "FILE VALIDATED" && status.destinations.length) {
            const nextRoles = [];
            status.destinations.sort((a, b) => a.sortOrder - b.sortOrder).forEach((dest) => {
              this.menuRoles.forEach((r) => {
                if (r.id === dest.destinationId) { nextRoles.push(r); rolesList.push(r); }
              });
            });
            role.nextRoles = nextRoles;
          }
        });
      }
    });
    this.activeMenuRoles = _.orderBy(this.activeMenuRoles, ["sortOrderNumber"]);
    this.activeMenuRoles = this.activeMenuRoles.filter((m) => {
      return this.isorHasDestionation(m);
    });
    this.activeMenuRoles.forEach((role) => {
      if (!this.isInOrderedMenuRoles(role)) {
        this.orderList.push(role);
      }
    });

    this.activeMenuRoles = Array.from(new Set(rolesList.map((a) => a.id)))
      .map((id) => {
        return rolesList.find((a) => a.id === id);
      }).filter((role) => role.sortOrderNumber !== 100);

    this.menuRoles.forEach((role) => {
      if (role.startRole) {
        if (!this.activeMenuRoles.includes(role)) {
          this.activeMenuRoles.push(role);
        }
      }
    });
    this.activeMenuRoles = this.activeMenuRoles.filter((r) => {
      return this.checkPathValidity(r);
    });
    this.activeMenuRoles.sort((x, y) => Number(y.startRole) - Number(x.startRole));
    this.menuRoles = _.differenceWith(this.menuRoles, this.activeMenuRoles, _.isEqual);
    if (this.isDeleted) {
      this.orderList = this.activeMenuRoles.filter((item) => !this.orderList.includes(item));
      this.isDeleted = false;
    }
    if (this.isStartRole) {
      this.orderList = [];
      this.activeMenuRoles.forEach((item) => {
        this.orderList.push(item);
      });
    }

    // for ghost roles
    this.ghostRoles = this.roles.filter((role) => role.isGhostInCamunda);
    this.ghostRoles.forEach((ghost) => {
      if (ghost.serverWhereGhostExists) {
        ghost.name += `<span class="badge badge-primary-light ml-2">${ghost.serverWhereGhostExists}</spn>`;
      }
    });
    // for unique roles load
    this.orderedMenuRoles = this.generateRolesList();
    // add branch length on nodes
    this.addBranchLength(this.orderList);
  }

  public addBranchLength(list: any[]) {
    list.forEach((e) => {
      if (e.nextRoles) {
        e.branchLength = 0;
        this.getBranchLength(e, e.nextRoles);
        this.addBranchLength(e.nextRoles);
      }
    });
  }

  public getBranchLength(branch, list: any[]) {
    if (list.length > 0) {
      branch.branchLength += 1;
      const nextLevel = [];
      list.forEach((n) => {
        if (n.nextRoles) {
          n.nextRoles.forEach((nr) => {
            nextLevel.push(nr);
          });
        }
      });
      this.getBranchLength(branch, nextLevel);
    }
  }

  public generateRolesList() {
    let newRolesList = [];
    // for startRoles
    const startRoles = [];
    const tempList = this.activeMenuRoles;
    tempList.forEach((role: any) => {
      if (role.startRole) {
        if (this.validBranch(role)) {
          startRoles.push(role);
        }
        role.isLoaded = true;
      }
    });
    if (startRoles.length) {
      newRolesList.push(startRoles);
      // middle levels
      newRolesList = this.getMiddleLevels(newRolesList, startRoles);
    }

    // for end roles
    const blankRoles = [];
    tempList.forEach((role: any) => {
      if (!role.nextRoles && !role.isLoaded && role.sortOrderNumber !== 100 && this.validBranch(role)) {
        blankRoles.push(role);
        role.isLoaded = true;
      }
    });
    /* if (blankRoles.length) {
      newRolesList.push(blankRoles);
    } */
    /* newRolesList = newRolesList.filter((m) => {
      return this.isorHasDestionation(m);
    }); */

    return newRolesList;
  }

  public getMiddleLevels(rolesList, roles) {
    // new level
    let level = [];
    // remove roles in nextRoles property
    roles = this.checkSiblingsRoles(roles);

    roles.forEach((role) => {
      if (role.nextRoles) {
        role.nextRoles.forEach((rol) => {
          if (this.validBranch(rol)) {
            rolesList = this.removeDuplicateRoleOnList(rolesList, rol);
            level = this.removeDuplicateRoleOnLevel(level, rol);
            level.push(rol);
            level = _.orderBy(level, ["branchLength"], ["asc"]);
          }
          this.markRolAsLoaded(rol);
        });
      }
    });
    if (level.length) {
      rolesList.push(level);
      return this.getMiddleLevels(rolesList, level);
    } else {
      return rolesList;
    }
  }

  public removeDuplicateRoleOnList(rolesList, role) {
    const aux = [];
    rolesList.forEach((group) => {
      const newgroup = group.filter((r) => r.id !== role.id);
      aux.push(Object.assign([], group.filter((r) => r.id !== role.id)));
      if (newgroup.length !== group.length) {
        group = newgroup;
      }
    });
    return aux;
  }

  public removeDuplicateRoleOnLevel(level, role) {
    return level.filter((r) => r.id !== role.id);
  }

  public markRolAsLoaded(rol) {
    this.orderList.forEach((role) => {
      if (role.id === rol.id) {
        role.isLoaded = true;
      }
    });
  }

  public checkSiblingsRoles(roles) {
    const nextLevelRoles = [];
    roles.forEach((role, index, object) => {
      const roleFilter = nextLevelRoles.filter((r) => r.id === role.id);
      if (roleFilter.length > 0) {
        object.splice(index, 1);
      } else {
        if (role.nextRoles) {
          role.nextRoles.forEach((rol) => {
            nextLevelRoles.push(rol);
          });
        }
      }
    });
    return roles;
  }

  private isInOrderedMenuRoles(element) {
    return this.orderList.some((omr) => {
      if (Array.isArray(omr)) {
        return omr.some((innerOmr) => {
          return innerOmr.id === element.id;
        });
      }
      return omr.id === element.id;
    });
  }

  public static filterUnnecessaryRoles(roles) {
    // remove applicant Guide form
    return roles.filter((role) => role.shortName !== "AP" && role.shortName !== "applicant");
  }

  public static filterFEBotRoles(roles) {
    // remove applicant Guide form
    return roles.filter((role) => !role.isFrontendBot);
  }

  public static filterGhostRoles(roles) {
    return roles.filter((role) => !role.isGhostInCamunda);
  }

  public deleteRole(role) {
    if (!Array.isArray(role)) {
      this.roleController.deleteRole(role.id).subscribe(
        (data) => {
          this.responseSuccessful("Role deleted!");
          this.isDeleted = true;
          this.ngOnInit();
          this.router.navigate(["/services/" + this.serviceId + "/roles"]);
        },
        (error) => this.showErrorMessage(error),
      );
    } else {
      const promises = [];
      role.forEach((r) => {
        promises.push(
          this.roleController.deleteRole(r.id).pipe(map(
            (data) => {
              return data;
            },
          )).toPromise(),
        );
      });
      Promise.all<any[]>(promises).then((results: any[]) => {
        this.responseSuccessful("Roles deleted!");
        this.isDeleted = true;
        this.ngOnInit();
        this.router.navigate(["/services/" + this.serviceId + "/roles"]);
      });
    }
  }

  public openNewRoleLayer() {
    this.showRoleAdd = true;
    this.hideLines();
  }

  public closeLayer() {
    this.showRoleAdd = false;
    this.showLines();
  }

  public validBranch(role): boolean {
    const determinantValid = this.checkDeterminantFilter(role);
    const registrationValid = this.checkRegistrationFilter(role);
    return determinantValid && registrationValid;
  }

  private checkDeterminantFilter(role): boolean {
    if (this.determinantFilters && this.determinantFilters.length && role.jsonDeterminants && JSON.parse(role.jsonDeterminants)) {
      const roleDets = JSON.parse(role.jsonDeterminants);
      if (roleDets[0] && roleDets[0].items && (roleDets[0].items.length < 1 || !role.jsonDeterminants.includes("determinantId"))) {
        return true;
      } else {
        return this.searchInDeterminants(roleDets);
      }
    } else if (this.determinantFilters && !this.determinantFilters.length && role.jsonDeterminants && JSON.parse(role.jsonDeterminants)) {
      const roleDets = JSON.parse(role.jsonDeterminants);
      if (roleDets[0] && roleDets[0].items && (roleDets[0].items.length < 1 || !role.jsonDeterminants.includes("determinantId"))) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  private checkRegistrationFilter(role): boolean {
    // If no registration filters selected, show all
    if (!this.registrationFilters || this.registrationFilters.length === 0) {
      return true;
    }

    // Get only selected (checked) registrations
    const selectedRegistrations = this.registrationFilters.filter(r => r.checked);

    // If all registrations are selected, show all (including roles with no registrations)
    if (selectedRegistrations.length === this.registrationFilters.length) {
      return true;
    }

    // If role has no registrations, hide it when specific filters are active
    if (!role.registrations || role.registrations.length === 0) {
      return false;
    }

    // OR logic: show role if it has ANY of the selected registrations
    return role.registrations.some((roleReg: RoleRegistration) => {
      return selectedRegistrations.some(selectedReg =>
        selectedReg.id === roleReg.registrationId
      );
    });
  }

  public searchInDeterminants(items) {
    if (items && items.length) {
      return this.determinantFilters.some((d) => {
        return items.some((i) => {
          if (i.determinantId && (i.determinantId === d.determinantId || i.id === d.determinantId)) {
            return true;
          } else if (i.items && items.length) {
            return this.searchInDeterminants(i.items);
          } else {
            return false;
          }
        });
      });
    } else {
      return false;
    }
  }

  public onDeterminantSelectionChanged(event): void {
    this.determinantFilters = event;
    this.orderedMenuRoles = this.generateRolesList();
  }

  private filterListActiveRoles() {
    if (this.determinantFilters.length === 0) {
      const filters = localStorage.getItem("determinantFilters");
      let determinants = [];
      determinants = JSON.parse(filters);
      if (determinants && determinants.length) {
        determinants.forEach((item) => {
          this.activeMenuRoles.push(this.roles.find((rol) => rol.id === item));
        });
      } else {
        this.activeMenuRoles = this.activeMenuRoles.filter((roles) => !this.determinantFilters.includes(roles.id));
      }
    } else {
      this.determinantFilters = Array.from(new Set(this.determinantFilters.map((a) => a)))
        .map((key) => {
          return this.determinantFilters.find((a) => a === key);
        });
      const filters = localStorage.getItem("determinantFilters");
      let determinants = [];
      determinants = JSON.parse(filters);
      if (determinants && determinants.length) {
        determinants = determinants.filter((e) => !this.determinantFilters.includes(e));
        if (determinants.length === 0) {
          this.activeMenuRoles = this.activeMenuRoles.filter((roles) => !this.determinantFilters.includes(roles.id));
        } else {
          determinants.forEach((item) => {
            this.activeMenuRoles.push(this.roles.find((rol) => rol.id === item));
          });
        }
      } else {
        this.activeMenuRoles = this.activeMenuRoles.filter((roles) => !this.determinantFilters.includes(roles.id));
      }
    }
    this.saveChanges("determinantFilters", this.determinantFilters);
  }

  public saveChanges(key: string, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  public onFillField(value) {
    this.roleNameField = value;
  }

}
